import { FC, useContext, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import {
  Input,
  Modal,
  Preloader,
  SearchIcon,
  Table,
  Tabs,
} from '@smartshell/ui-kit';
import { useSearchParams } from 'react-router-dom';

import { AuthContext } from 'auth';
import { useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import {
  ClientModal,
  NotPermissionModal,
  PostPaymentsModal,
  DepositModal,
  ResetPasswordModal,
} from 'modals';
import { ErrorHandler } from 'services/ErrorHandler';
import { SettingsContext } from 'settings';
import { EPaymentMethod, IUser, TPayment } from 'types';

import { DATE_KEY } from './constants';
import { TTabPayment } from './interfaces';
import s from './Payments.module.scss';
import { GET_ACTIVE_PAYMENTS, IGetActivePaymentsResponse } from './schema';
import { Sidebar } from './Sidebar';
import { TableMobile } from './TableMobile';
import { useColumns, getTabs } from './utils';

export const Payments: FC = () => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { permissions, isActiveOperator },
  } = useContext(AuthContext);
  const {
    state: {
      settings: {
        currency: { sign: currency },
      },
    },
  } = useContext(SettingsContext);
  const size = useWindowResize();
  const { data: activePaymentsResponse, loading } =
    useQuery<IGetActivePaymentsResponse>(GET_ACTIVE_PAYMENTS, {
      fetchPolicy: 'cache-and-network',
      skip: !permissions?.has('work_shift_active'),
    });

  const loadedPayments =
    activePaymentsResponse?.activeWorkShift?.payments ?? [];

  const [clientUUID, setClientUUID] = useState<string | null>(null);
  const [selectedPaymentId, setSelectedPaymentId] = useState<number | null>(
    null
  );
  const [notPermissionModalOpen, setNotPermissionModalOpen] =
    useState<boolean>(false);
  const selectedPayment = useMemo(
    () => loadedPayments.find((payment) => payment.id === selectedPaymentId),
    [loadedPayments, selectedPaymentId]
  );
  const [isResetPasswordModal, setIsResetPasswordModal] = useState<
    boolean | undefined
  >(false);
  const [currentClient, setCurrentClient] = useState<IUser | undefined>(
    undefined
  );

  const [isPostPaymentsModalOpen, setIsPostPaymentsModalOpen] = useState(false);

  const [search, setSearch] = useState('');
  const payments = useMemo<Array<TPayment>>(
    () =>
      loadedPayments.filter((payment) =>
        JSON.stringify(payment).toLowerCase().includes(search.toLowerCase())
      ) ?? [],
    [search, loadedPayments]
  );

  const postPayments = useMemo<Array<TPayment>>(
    () => payments.filter(({ method }) => method === EPaymentMethod.POSTPAID),
    [payments]
  );

  const [type, setType] = useState<TTabPayment>('ALL');

  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('type') === 'POST_PAYMENTS') {
      setType('POST_PAYMENTS');
    }
  }, [searchParams]);

  const [isDepositOpen, setIsDepositOpen] = useState(false);
  const [defaultClient, setIsDefaultClient] = useState<string | undefined>('');

  const columns = useColumns(
    setClientUUID,
    currency,
    t,
    permissions,
    setNotPermissionModalOpen,
    size.width
  );

  const TABS = useMemo(() => getTabs(t), [t]);
  const mobileItems = type === 'ALL' ? payments : postPayments;

  return (
    <ErrorHandler>
      <Preloader
        loading={loading}
        hasData={Boolean(loadedPayments.length)}
        className={s.preloader}
        noDataPlaceholder={t(
          isActiveOperator
            ? 'workShift.payments.placeholder'
            : 'workShift.payments.manager.placeholder'
        )}
      >
        <div className={s.container}>
          {notPermissionModalOpen && (
            <NotPermissionModal
              onClose={() => setNotPermissionModalOpen(false)}
            />
          )}
          {isDepositOpen && (
            <DepositModal
              defaultClientUUID={defaultClient}
              onClose={() => {
                setIsDepositOpen(false);
                setClientUUID(null);
              }}
            />
          )}
          {isResetPasswordModal && (
            <ResetPasswordModal
              currentClient={currentClient}
              onClose={() => setIsResetPasswordModal(false)}
              clientUUID={clientUUID}
            />
          )}
          {clientUUID && !isDepositOpen && !isResetPasswordModal && (
            <ClientModal
              clientUUID={clientUUID}
              onClose={() => setClientUUID(null)}
              setIsDepositOpen={setIsDepositOpen}
              setIsDefaultClient={setIsDefaultClient}
              setIsResetPasswordModal={setIsResetPasswordModal}
              setCurrentClient={setCurrentClient}
            />
          )}
          {isPostPaymentsModalOpen && (
            <PostPaymentsModal
              payment={selectedPayment ?? null}
              onClose={() => setIsPostPaymentsModalOpen(false)}
            />
          )}
          {size.width <= 960 && selectedPayment && (
            <Modal
              className={s.modal}
              onClose={() => setSelectedPaymentId(null)}
            >
              <Sidebar
                payment={selectedPayment ?? null}
                openPostPaymentModal={() => setIsPostPaymentsModalOpen(true)}
                setSelectedPaymentId={setSelectedPaymentId}
                size={size.width}
              />
            </Modal>
          )}
          {(size.width > 1864 || (selectedPayment && size.width > 960)) && (
            <Sidebar
              payment={selectedPayment ?? null}
              openPostPaymentModal={() => setIsPostPaymentsModalOpen(true)}
              setSelectedPaymentId={setSelectedPaymentId}
              size={size.width}
            />
          )}

          <div className={s.top}>
            <Tabs
              options={TABS}
              onChange={setType}
              value={type}
              className={s.tabs}
              itemVariant="row"
            />
            <Input
              value={search}
              onChange={setSearch}
              leftContent={<SearchIcon color="primary" />}
              placeholder={t('payments.search')}
              errorWrapperClassName={s['search']}
              variant="secondary"
            />
          </div>

          {size.width > 960 ? (
            <Table
              data={mobileItems}
              columns={columns}
              onRowClick={(event, row, table) => {
                const isClickableElement = (
                  event.target as HTMLElement
                ).closest('[data-prevent-row-click]');

                if (isClickableElement) return;

                table.toggleAllRowsSelected(false);
                row.toggleRowSelected(!row.isSelected);
                setSelectedPaymentId(
                  selectedPayment?.id === row.original.id
                    ? null
                    : row.original.id
                );
              }}
              sortBy={[{ id: DATE_KEY, desc: true }]}
              className={s.table}
            />
          ) : (
            mobileItems
              ?.sort(
                (a, b) =>
                  new Date(b.created_at).getTime() -
                  new Date(a.created_at).getTime()
              )
              ?.map((data) => (
                <TableMobile
                  key={data?.id}
                  setNotPermissionModalOpen={setNotPermissionModalOpen}
                  setClientId={setClientUUID}
                  data={data}
                  setSelectedPaymentId={setSelectedPaymentId}
                />
              ))
          )}
        </div>
      </Preloader>
    </ErrorHandler>
  );
};
